<template>
  <div
    class="relative flex flex-col min-w-0 break-words bg-white w-full border border-blueGray-200 rounded-lg"
  >
    <div class="rounded-t mb-0 px-4 py-3 border-0">
      <div class="flex flex-wrap items-center">
        <div class="relative w-full max-w-full flex-grow flex-1">
          <h3 class="text-base text-blueGray-700">
            <i class="pi pi-list-check"></i> Plan List
          </h3>
        </div>
        <div
          class="relative w-full px-4 max-w-full flex-grow flex-1 flex flex-wrap items-center justify-end"
          style="gap: 0.25rem;"
        >
          <select
            v-model="selectedType"
            class="border-0 px-2 py-2 placeholder-blueGray-300 text-blueGray-600 capitalize bg-blueGray-100 rounded text-xs shadow focus:outline-none ease-linear transition-all duration-150 mr-1"
          >
            <option value="all">All types</option>
            <option v-for="type in planTypes" :key="type" :value="type" class="capitalize">{{ type }}</option>
          </select>
          <button
            class="bg-blueGray-900 text-white active:bg-blueGray-800 text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150 max-w-full"
            type="button"
            @click="$emit('create')"
            v-if="has('plans.store')"
          >
            <i class="pi pi-plus-circle" style="font-size: 10px;"></i> Create new
          </button>
        </div>
      </div>
    </div>
    <div class="block w-full overflow-x-auto">
      <!-- Projects table -->
      <table class="items-center w-full bg-transparent border-collapse">
        <thead class="thead-light">
          <tr>
            <th
              class="px-4 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left"
            >
              <i class="pi pi-user mr-1"></i>Name
            </th>
            <th
              class="px-4 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left"
            >
              <i class="pi pi-tag mr-1"></i>Type
            </th>
            <th
              class="px-4 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left"
            >
              <i class="pi pi-dollar mr-1"></i>Amount
            </th>
            <th
              class="px-4 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left"
            >
              <i class="pi pi-users mr-1"></i>Total Subs
            </th>
            <th
              class="px-4 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left"
            >
              <i class="pi pi-user-plus mr-1"></i>Active Subs
            </th>
            <th
              class="px-4 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left"
            >
              <i class="pi pi-dollar mr-1"></i>Total Amount
            </th>
            <th
              class="px-4 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left"
            >
              <i class="pi pi-flag mr-1"></i>Status
            </th>
            <th
              class="px-4 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left"
            >
              <i class="pi pi-cog mr-1"></i>Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-if="!loaders().getPlan">
            <tr v-for="(plan, i) in pagedPlans" :key="i" class="cursor-pointer" @click="selectPlan(plan)">
              <th
                class="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-2 px-4 text-left"
                :class="{'line-through italic text-blueGray-400': !plan.is_active}"
              >
                <i class="pi pi-pen-to-square"></i> {{ plan.name }}
              </th>
              <td
                class="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-2 px-4 capitalize"
              >
                {{ plan.type }}
              </td>
              <td
                class="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-2 px-4 text-xs"
              >
                {{ formatAbbrCurrency({currency: plan.base_currency, amount: plan.min_amount}) }} -
                {{ formatAbbrCurrency({currency: plan.base_currency, amount: plan.max_amount}) }}
              </td>
              <td
                class="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-2 px-4"
              >
                {{ plan.subscribers_count || 0 }}
              </td>
              <td
                class="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-2 px-4"
              >
                {{ plan.active_subscribers_count || 0 }}
              </td>
              <td
                class="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-2 px-4"
              >
                {{ formatAbbrCurrency({currency: plan.base_currency, amount: plan.total_amount || 0}) }}
              </td>
              <td
                class="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-2 px-4"
              >
                <span :class="plan.is_active ? 'text-blueGray-900' : 'text-orange-500'">
                  {{ plan.is_active ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td
                class="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-2 px-4 text-right"
                @click.stop
              >
                <plan-table-dropdown
                  :plan="plan"
                  @view-click="() => viewPlan(plan)"
                  @edit-click="() => selectPlan(plan)"
                  @deactivate-click="() => toggleActive(plan)"
                  @delete-click="() => removePlan(plan)"
                />
              </td>
            </tr>
          </template>
          <template v-else>
            <tr v-for="n in 6" :key="n">
              <td v-for="col in 8" :key="col" class="border-t-0 align-middle border-l-0 border-r-0 py-2 px-4">
                <div class="h-3 bg-blueGray-200 rounded animate-pulse" :style="{ width: ['85%','60%','70%','50%','65%','55%','45%'][(col-1) % 7] }"></div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <empty-list v-if="filteredPlans.length === 0 && !loaders().getPlan"></empty-list>
    </div>
    <div class="flex flex-wrap items-center" v-if="filteredPlans.length > 0">
      <div class="relative w-full px-4 max-w-full flex-grow flex-1">
        <circular-pagination :current_page="currentPage" :last_page="lastPage" :size="pageSize" @paginate="pageChange"></circular-pagination>
      </div>
    </div>

    <transition name="bounce" :duration="{ enter: 500, leave: 800 }">
      <div v-if="modals.plan" style="width: 100%;" @click.self="$_close_all_modal" class="overflow-x-hidden overflow-y-auto fixed inset-0 z-40 outline-none focus:outline-none justify-center items-center flex w-screen h-screen top-0 left-0">
        <div class="relative w-auto z-50">
          <!--content-->
          <div class="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
            <!--header-->
            <div class="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
              <h3 class="text-lg capitalize">
                <i class="pi pi-info-circle"></i> {{ viewingPlan?.name }} information
              </h3>
              <button
                class="p-1 ml-auto bg-transparent border-0 text-black opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                v-on:click="toggleModal('plan', false)"
              >
                <span class="bg-transparent text-black opacity-5 h-6 w-6 text-2xl block outline-none focus:outline-none">
                  ×
                </span>
              </button>
            </div>
            <!--body-->
            <div class="relative overflow-auto" style="max-height: 460px;">
              <recursive-table
                :data="viewingPlan"
                :excludeAll="['id', 'configs', 'coin_types']"
                :datesAll="['created_at', 'updated_at']"
              />
            </div>
            <div class="flex items-center justify-end p-4 border-t border-solid border-blueGray-200 rounded-b">
              <button
                class="text-red-500 background-transparent px-3 py-1 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150" type="button"
                v-on:click="toggleModal('plan', false)"
              >
                close
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <div v-if="modals.plan" @click="$_close_all_modal" style="width: 100%; opacity: 0.25;" class="opacity-25 fixed inset-0 z-30 h-screen top-0 left-0 bg-black"></div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { formatAbbrCurrency, confirmAction } from '@/utils'
import PlanTableDropdown from "@/components/Dropdowns/PlanTableDropdown.vue";
import RecursiveTable from '@/components/Tables/RecursiveTable.vue';
import EmptyList from "@/components/Cards/EmptyList.vue";
import CircularPagination from "@/components/Paginations/CircularPagination.vue";
import { modal } from '@/mixins'

export default {
  props: ["plans"],
  mixins: [modal],
  components: {
    PlanTableDropdown,
    RecursiveTable,
    EmptyList,
    CircularPagination
  },
  data() {
    return {
      modals: {},
      viewingPlan: {},
      // The plans list endpoint isn't paginated server-side (other consumers — the
      // product-plan picker, plan-type stats — need the complete list), so pagination
      // here is purely client-side over the already-fetched array. The type filter
      // below is client-side for the same reason — the full list is already on hand.
      pageSize: 10,
      currentPage: 1,
      selectedType: 'all'
    }
  },
  computed: {
    planTypes() {
      return [...new Set((this.plans || []).map(p => p.type).filter(Boolean))];
    },
    filteredPlans() {
      if (this.selectedType === 'all') return this.plans || [];
      return (this.plans || []).filter(p => p.type === this.selectedType);
    },
    lastPage() {
      return Math.max(1, Math.ceil(this.filteredPlans.length / this.pageSize));
    },
    pagedPlans() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredPlans.slice(start, start + this.pageSize);
    }
  },
  watch: {
    plans() {
      if (this.currentPage > this.lastPage) {
        this.currentPage = this.lastPage;
      }
    },
    selectedType() {
      this.currentPage = 1;
    }
  },
  created() {
    this.formatAbbrCurrency = formatAbbrCurrency;
  },
  methods: {
    ...mapActions(["deletePlan", "updatePlan"]),
    ...mapGetters(["loaders"]),
    pageChange(pagination) {
      this.currentPage = pagination.page;
    },
    selectPlan(plan) {
      if( this.has('plans.show') || this.has('plans.update') || this.has('plans.store') ) {
        this.$emit('selectedPlan', plan)
      }
    },
    viewPlan(plan) {
      this.viewingPlan = plan;
      this.toggleModal('plan', true)
    },
    toggleActive(plan) {
      const nextState = !plan.is_active;
      confirmAction({
        confirmText: `${nextState ? 'Activate' : 'Deactivate'} ${plan.name}`,
        confirmTextHelper: `Kindly confirm you want to ${nextState ? 'activate' : 'deactivate'} this plan`,
        successText: `You have successfully ${nextState ? 'activated' : 'deactivated'} ${plan.name}`,
        cancelText: `${nextState ? 'Activation' : 'Deactivation'} of plan aborted`
      }, () => {
        this.updatePlan({...plan, is_active: nextState})
      });
    },
    removePlan(plan) {
      confirmAction({
        confirmText: `Delete ${plan.name}`,
        confirmTextHelper: `Kindly confirm you want to permanently delete this plan. This cannot be undone.`,
        successText: `You have successfully deleted ${plan.name}`,
        cancelText: `Deletion of plan aborted`
      }, () => {
        this.deletePlan(plan)
      });
    }
  }
}
</script>
